import { ArrowUpRight, MapPin, Ruler, Clock } from "lucide-react";

const PRIMA = "https://customer-assets.emergentagent.com/job_costruzioni-modern/artifacts/4lkwd0bg_VIA%20MILANO%20-%20VIGEVANO%20PRIMA.jpg";
const DURANTE = "https://customer-assets.emergentagent.com/job_costruzioni-modern/artifacts/ebh2pque_VIA%20MILANO%20VIGEVANO%20DURANTE.jpg";
const DOPO = "https://customer-assets.emergentagent.com/job_costruzioni-modern/artifacts/4e3cgn0d_VIA%20MILANO%20VIGEVANO%20DOPO.jpg";

const OTHER_PROJECTS = [
  {
    title: "Ristrutturazione appartamento",
    location: "Pavia · 2024",
    cat: "Chiavi in mano",
    img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    size: "wide",
  },
  {
    title: "Cappotto termico residenziale",
    location: "Milano · 2024",
    cat: "Cappotto",
    img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    size: "square",
  },
  {
    title: "Finiture d'interni",
    location: "Milano · 2024",
    cat: "Finiture",
    img: "https://static.prod-images.emergentagent.com/jobs/4f3190bd-1b22-40d9-b386-e9731f443b12/images/6faf46cdddc3fbf877b326011bdfe3753c876cb091ac792b6b28acb88b6bda00.png",
    size: "square",
  },
];

export default function Gallery() {
  return (
    <section
      id="progetti"
      data-testid="gallery-section"
      className="relative bg-[#050B14] py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-px bg-[#FF5A1F]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
                Progetti
              </span>
            </div>
            <h2 className="headline text-white text-4xl md:text-6xl">
              Selezione{" "}
              <span className="font-display italic font-medium text-slate-300">
                cantieri
              </span>{" "}
              recenti.
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-slate-400 text-base md:text-lg leading-relaxed">
              Uno sguardo trasversale ai cantieri realizzati tra Pavia e Milano:
              facciate, interni, carpenteria e ristrutturazioni complete.
            </p>
          </div>
        </div>

        {/* Featured Case Study - Via Milano Vigevano */}
        <article
          data-testid="case-study-via-milano"
          className="mb-12 bg-[#0A1120] border border-[#1E293B]"
        >
          {/* Case study header */}
          <div className="p-6 md:p-10 border-b border-[#1E293B]">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="text-[10px] tracking-[0.25em] uppercase bg-[#FF5A1F] text-white px-2.5 py-1 font-bold">
                Case Study
              </span>
              <span className="text-[10px] tracking-[0.25em] uppercase border border-slate-600 text-slate-300 px-2.5 py-1 font-bold">
                Facciate
              </span>
              <span className="text-[10px] tracking-[0.25em] uppercase border border-slate-600 text-slate-300 px-2.5 py-1 font-bold">
                Restauro Storico
              </span>
              <span className="text-[10px] tracking-[0.25em] uppercase border border-slate-600 text-slate-300 px-2.5 py-1 font-bold">
                Anni '20
              </span>
            </div>
            <h3 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
              Via Milano,{" "}
              <span className="font-display italic font-medium text-slate-300">
                Vigevano
              </span>
            </h3>
            <p className="mt-5 text-slate-300 max-w-3xl leading-relaxed text-[15px] md:text-base">
              Restauro conservativo di una <span className="text-white font-semibold">palazzina d'epoca degli anni '20</span>, eseguito secondo i criteri storici prescritti dall'architetto incaricato.
            </p>
            <p className="mt-3 text-slate-400 max-w-3xl leading-relaxed text-[15px]">
              L'intervento ha previsto il <span className="text-white">consolidamento strutturale</span> della facciata, il <span className="text-white">recupero integrale dei bow-window</span> e il restauro dei dettagli ornamentali in stile liberty. Tinteggiature realizzate con <span className="text-white">colori a calce d'epoca</span>, fedeli alla palette originale dell'edificio. Tutte le fasi sono state condotte con i nostri ponteggi proprietari e con squadra interna.
            </p>
            <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { l: "Località", v: "Vigevano (PV)" },
                { l: "Epoca", v: "Anni '20" },
                { l: "Lavorazioni", v: "Consolidamento + Restauro" },
                { l: "Finitura", v: "Pittura a calce d'epoca" },
              ].map((d) => (
                <div key={d.l} className="border-l-2 border-[#FF5A1F] pl-4">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold mb-1">
                    {d.l}
                  </div>
                  <div className="text-white font-semibold text-sm">{d.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Three-phase showcase */}
          <div className="grid md:grid-cols-3 gap-px bg-[#1E293B]">
            {[
              { label: "Prima", desc: "Facciata anni '20 con degrado strutturale, bow-window compromessi e decorazioni in stile liberty da recuperare.", img: PRIMA, color: "text-slate-400" },
              { label: "Durante", desc: "Consolidamento strutturale, restauro dei bow-window e tracciamento dei decori geometrici originali sui nostri ponteggi proprietari.", img: DURANTE, color: "text-[#FF5A1F]" },
              { label: "Dopo", desc: "Palazzina riportata alla bellezza dell'epoca: tinteggiature a calce d'epoca, bow-window integri, ornamenti restaurati secondo i criteri dell'architetto.", img: DOPO, color: "text-[#22C55E]" },
            ].map((phase, i) => (
              <div
                key={phase.label}
                data-testid={`phase-${phase.label.toLowerCase()}`}
                className="bg-[#0A1120] flex flex-col"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#111C30]">
                  <img
                    src={phase.img}
                    alt={`${phase.label} - Restauro facciata Via Milano Vigevano`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`text-xs tracking-[0.3em] uppercase font-bold px-3 py-1.5 bg-[#050B14]/85 backdrop-blur ${phase.color}`}>
                      / 0{i + 1} {phase.label}
                    </span>
                  </div>
                </div>
                <div className="p-5 md:p-6 flex-1">
                  <div className={`text-xs tracking-[0.25em] uppercase font-bold mb-2 ${phase.color}`}>
                    {phase.label}
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {phase.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA bar */}
          <div className="p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-[#1E293B]">
            <div className="text-slate-300 text-sm md:text-base">
              Hai un edificio storico o una facciata d'epoca da recuperare?{" "}
              <span className="text-white font-semibold">
                Sopralluogo gratuito in 24h.
              </span>
            </div>
            <a
              href="#preventivo"
              data-testid="case-study-cta"
              className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#EA450C] text-white px-5 py-3 text-sm font-semibold transition-colors whitespace-nowrap"
            >
              Richiedi preventivo facciata <ArrowUpRight size={16} />
            </a>
          </div>
        </article>

        {/* Section: other projects */}
        <div className="mt-20 mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-px bg-slate-600" />
            <span className="text-xs tracking-[0.3em] uppercase text-slate-500 font-bold">
              Altri Cantieri
            </span>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
            Una selezione del nostro lavoro
          </h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 auto-rows-[200px] md:auto-rows-[260px]">
          {OTHER_PROJECTS.map((p, i) => {
            const span = p.size === "wide" ? "md:col-span-2" : "";
            return (
              <a
                href="#preventivo"
                key={i}
                data-testid={`project-${i}`}
                className={`gallery-item group relative ${span} bg-[#111C30]`}
              >
                <img
                  src={p.img}
                  alt={p.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/30 to-transparent opacity-90 group-hover:opacity-70 transition-opacity" />
                <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] tracking-[0.25em] uppercase bg-[#FF5A1F] text-white px-2 py-1 font-bold">
                      {p.cat}
                    </span>
                    <ArrowUpRight
                      size={20}
                      className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-300 mb-1">
                      {p.location}
                    </div>
                    <div className="text-white font-bold text-base md:text-lg leading-tight tracking-tight">
                      {p.title}
                    </div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <p className="text-slate-500 text-sm italic">
            Altre foto dei nostri cantieri in arrivo nelle prossime settimane.
          </p>
        </div>
      </div>
    </section>
  );
}
